import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { BoardPanel } from '../board/BoardPanel';
import { BrassButton } from '../board/BrassButton';
import { Confetti } from '../board/Confetti';
import { TitlePlaque } from '../board/TitlePlaque';
import { animate, COLOURS, displayStyle, ease, uiStyle } from '../board/ui';
import { GemGauge } from './GemGauge';
import type { SlotUnlockMode } from '../../logic/unlock/slotUnlockMode';
import { unlockGemHint } from '../../logic/slot/gemText';

/**
 * What the slot unlock board is showing (`slotUnlockMode` picks it):
 * - `offer`: opened unasked, because the player has enough gems for a slot;
 * - `unlock`: a locked slot was tapped and the player can open it;
 * - `short`: a locked slot was tapped and the player needs more gems;
 * - `downloading`: bought, its art and sound are on their way;
 * - `ready`: downloaded, the slot is opening;
 * - `none`: the tile isn't a slot (should never show).
 */
export type { SlotUnlockMode };

/** What the player pressed: open the slot, or close the board. */
export type SlotUnlockPress = 'buy' | 'cancel';

export interface SlotUnlockProgress {
  filesLoaded: number;
  filesTotal: number;
  bytesLoaded: number;
  bytesTotal: number;
}

export interface SlotUnlockView {
  mode: SlotUnlockMode;
  /** The slot's name. */
  name: string;
  /** Its price in gems. */
  price: number;
  /** The player's gems. */
  gems: number;
  /** While downloading; null before the first file. */
  progress?: SlotUnlockProgress | null;
  /** Gems land on the player's reels (reel_gems): the hint says so. */
  reelGems?: boolean;
}

export interface SlotUnlockArt {
  floor: Texture | null;
  gem: Texture | null;
  lock: Texture | null;
  check: Texture | null;
  track: Texture | null;
  fill: Texture | null;
}

/** The board as plain data, for tests. */
export interface SlotUnlockUi {
  mode: SlotUnlockMode;
  title: string;
  lines: string[];
  buttons: string[];
  /** How full the gauge shows (0 to 1), or null when there's none. */
  gauge: number | null;
  /** The slot's picture is showing (else the lock). */
  preview: boolean;
}

const TITLES: Record<SlotUnlockMode, string> = {
  offer: 'NEW SLOT!',
  unlock: 'UNLOCK SLOT',
  short: 'UNLOCK SLOT',
  downloading: 'DOWNLOADING',
  ready: 'READY!',
  none: 'UNLOCK',
};

const MAX_BOARD_W = 360;
const PAD = { top: 34, side: 20, bottom: 20 };
const MARGIN = 12;
const BUTTON_H = 50;
const GAP = 10;

/**
 * Gems open new slots (producer 2026-10-12, "it's not clear that gems
 * unlock slots"): the board that offers a slot the player can afford, opens
 * a locked one they tapped, or says how many more gems it needs, then shows
 * its download. Drawn on the board kit (producer 2026-10-14: every dialog
 * in Pixi, painted, action on the right and cancel on the left).
 */
export class SlotUnlockBoard extends Container {
  private readonly dim = new Graphics();
  private readonly card = new Container();
  private readonly board: BoardPanel;
  private readonly plaque = new TitlePlaque(TITLES.unlock, 17);
  private readonly content = new Container();
  private readonly confetti = new Confetti();

  private readonly haveRow = new Container();
  private readonly haveText = new Text({ text: '', style: uiStyle(17, COLOURS.cream, '600') });
  private readonly haveGem = new Sprite();
  private readonly lead = new Text({ text: '', style: { ...uiStyle(15, COLOURS.cream, '500'), align: 'center', wordWrap: true } });
  private readonly picture = new Sprite();
  private readonly lockIcon = new Sprite();
  private readonly nameRow = new Container();
  private readonly checkIcon = new Sprite();
  private readonly nameText = new Text({ text: '', style: { ...displayStyle(26), align: 'center', wordWrap: true } });
  private readonly keep = new Text({ text: 'Yours to keep, forever.', style: { ...uiStyle(15, COLOURS.cream, '500'), align: 'center' } });
  private readonly gaugeLabel = new Text({ text: 'Your gems', style: uiStyle(14, COLOURS.cream, '600') });
  private readonly gaugeCount = new Text({ text: '', style: displayStyle(17) });
  private readonly gauge: GemGauge;
  private readonly toGoRow = new Container();
  private readonly toGoText = new Text({ text: '', style: displayStyle(20) });
  private readonly toGoGem = new Sprite();
  private readonly toGoTail = new Text({ text: 'to go', style: displayStyle(20) });
  private readonly hint = new Text({ text: '', style: { ...uiStyle(14, COLOURS.cream, '500'), align: 'center', wordWrap: true } });
  private readonly footnote = new Text({ text: '', style: { ...uiStyle(13, COLOURS.cream, '500'), align: 'center' } });
  private readonly cancelButton: BrassButton;
  private readonly actionButton: BrassButton;
  private readonly actionGem = new Sprite();

  private view: SlotUnlockView = { mode: 'unlock', name: '', price: 0, gems: 0 };
  private viewW = 1;
  private viewH = 1;
  private contentW = 300;
  private opened = false;
  private celebrated = false;

  constructor(
    private readonly art: SlotUnlockArt,
    private readonly onPress: (press: SlotUnlockPress) => void,
  ) {
    super();
    this.board = new BoardPanel({ floor: art.floor });
    this.gauge = new GemGauge({ track: art.track, fill: art.fill });
    this.dim.eventMode = 'static';
    this.dim.on('pointertap', () => {
      if (this.dismissable) this.onPress('cancel');
    });
    for (const gem of [this.haveGem, this.toGoGem, this.actionGem]) {
      if (art.gem) gem.texture = art.gem;
      gem.anchor.set(0, 0.5);
    }
    if (art.lock) this.lockIcon.texture = art.lock;
    if (art.check) this.checkIcon.texture = art.check;
    this.picture.anchor.set(0.5, 0);
    this.lockIcon.anchor.set(0.5, 0);
    this.checkIcon.anchor.set(0, 0.5);
    this.haveText.anchor.set(0, 0.5);
    this.toGoText.anchor.set(0, 0.5);
    this.toGoTail.anchor.set(0, 0.5);
    this.nameText.anchor.set(0, 0.5);
    this.haveRow.addChild(this.haveText, this.haveGem);
    this.toGoRow.addChild(this.toGoText, this.toGoGem, this.toGoTail);
    this.nameRow.addChild(this.checkIcon, this.nameText);
    this.cancelButton = new BrassButton({ label: 'Not now', kind: 'wood', size: 17, height: BUTTON_H, onTap: () => this.onPress('cancel') });
    this.actionButton = new BrassButton({ label: 'UNLOCK', kind: 'gold', size: 17, height: BUTTON_H, onTap: () => this.pressAction() });
    this.actionButton.addChild(this.actionGem);
    // The gem rides down with the plate's words when it's pressed.
    this.actionButton.on('pointerdown', () => (this.actionGem.y = BUTTON_H / 2 + 2));
    const up = () => (this.actionGem.y = BUTTON_H / 2);
    this.actionButton.on('pointerup', up);
    this.actionButton.on('pointerupoutside', up);
    this.content.addChild(
      this.haveRow,
      this.lead,
      this.picture,
      this.lockIcon,
      this.nameRow,
      this.keep,
      this.gaugeLabel,
      this.gaugeCount,
      this.gauge,
      this.toGoRow,
      this.hint,
      this.footnote,
      this.cancelButton,
      this.actionButton,
    );
    this.card.addChild(this.board, this.content, this.plaque, this.confetti);
    this.addChild(this.dim, this.card);
    this.visible = false;
  }

  /** Show a view (or the same mode with new numbers). */
  show(view: SlotUnlockView): void {
    this.view = view;
    // Before it opens, open() fills the gauge from empty.
    if (this.opened) this.gauge.setValue(this.gaugeTarget());
    this.layout(this.viewW, this.viewH);
    if (view.mode === 'ready' && !this.celebrated && this.opened) {
      this.celebrated = true;
      this.celebrate();
    }
  }

  /** Confetti from the plaque, up and out, clear of the words. */
  private celebrate(): void {
    void this.confetti.burst(this.board.boardWidth / 2, 0, { count: 90, spread: Math.max(260, this.board.boardWidth) });
  }

  /** The slot's picture arrived (its map tile). */
  setPicture(texture: Texture | null): void {
    if (!texture || this.picture.texture === texture) return;
    this.picture.texture = texture;
    this.layout(this.viewW, this.viewH);
  }

  get mode(): SlotUnlockMode {
    return this.view.mode;
  }

  /** Open with the board's pop, and the gauge filling to the gems. */
  async open(): Promise<void> {
    this.visible = true;
    this.opened = true;
    this.alpha = 0;
    const target = this.gaugeTarget();
    this.gauge.setValue(0);
    await animate(220, (t) => {
      this.alpha = ease.outCubic(t);
      this.card.scale.set(this.fitScale * (0.92 + 0.08 * ease.outBack(t)));
    });
    if (this.view.mode === 'ready' && !this.celebrated) {
      this.celebrated = true;
      this.celebrate();
    }
    if (this.hasGauge && this.view.mode !== 'downloading') await this.gauge.fillTo(target);
    else this.gauge.setValue(this.gaugeTarget());
  }

  layout(viewW: number, viewH: number): void {
    this.viewW = viewW;
    this.viewH = viewH;
    this.dim.clear().rect(0, 0, viewW, viewH).fill({ color: 0x0a0602, alpha: 0.72 });
    const boardW = Math.min(MAX_BOARD_W, Math.max(260, viewW - 2 * MARGIN));
    this.contentW = boardW - 2 * PAD.side;
    this.plaque.setTitle(TITLES[this.view.mode]);
    const contentH = this.layoutContent();
    const boardH = contentH + PAD.top + PAD.bottom;
    this.board.resize(boardW, boardH);
    this.content.position.set(PAD.side, PAD.top);
    this.plaque.position.set(boardW / 2, 0);
    const rise = this.plaque.plateHeight / 2;
    const room = viewH - 2 * MARGIN;
    this.fitScale = Math.min(1, room / (boardH + rise));
    this.card.scale.set(this.fitScale);
    this.card.pivot.set(boardW / 2, boardH / 2);
    const cardH = (boardH + rise) * this.fitScale;
    const top = Math.max(MARGIN, (viewH - cardH) / 2);
    this.card.position.set(Math.round(viewW / 2), Math.round(top + rise * this.fitScale + (boardH / 2) * this.fitScale));
  }

  private fitScale = 1;

  /** The board as plain data, for tests. */
  ui(): SlotUnlockUi {
    const shown = (t: Text) => t.visible && t.text.trim() !== '';
    const lines: string[] = [];
    if (this.haveRow.visible) lines.push(this.haveText.text.trim());
    for (const t of [this.lead, this.nameText, this.keep, this.gaugeLabel, this.gaugeCount]) if (shown(t)) lines.push(t.text);
    if (this.toGoRow.visible) lines.push(`${this.toGoText.text} ${this.toGoTail.text}`);
    for (const t of [this.hint, this.footnote]) if (shown(t)) lines.push(t.text);
    return {
      mode: this.view.mode,
      title: TITLES[this.view.mode],
      lines,
      buttons: this.buttons().map((b) => b.labelText.trim()),
      gauge: this.hasGauge ? Math.round(this.gauge.fraction * 1000) / 1000 : null,
      preview: this.picture.visible,
    };
  }

  /** A button's middle in canvas pixels (tests tap there), by its label. */
  where(label: string): { x: number; y: number } | null {
    const button = this.buttons().find((b) => b.labelText.trim() === label);
    if (!button) return null;
    const p = button.toGlobal({ x: button.buttonWidth / 2, y: button.buttonHeight / 2 });
    return { x: p.x, y: p.y };
  }

  /** Press a button by its label (tests). */
  press(label: string): boolean {
    const button = this.buttons().find((b) => b.labelText.trim() === label);
    if (!button) return false;
    button.onTap?.();
    return true;
  }

  private buttons(): BrassButton[] {
    return [this.cancelButton, this.actionButton].filter((b) => b.visible);
  }

  private get dismissable(): boolean {
    const mode = this.view.mode;
    return mode !== 'downloading' && mode !== 'ready';
  }

  private get hasGauge(): boolean {
    const mode = this.view.mode;
    return mode === 'unlock' || mode === 'short' || mode === 'downloading';
  }

  private gaugeTarget(): number {
    const v = this.view;
    if (v.mode === 'downloading') {
      const p = v.progress;
      return p && p.filesTotal > 0 ? p.filesLoaded / p.filesTotal : 0;
    }
    return v.price > 0 ? v.gems / v.price : 1;
  }

  private pressAction(): void {
    const mode = this.view.mode;
    if (mode === 'offer' || mode === 'unlock') this.onPress('buy');
    else this.onPress('cancel');
  }

  /** Lay the content out for the current view; returns its height. */
  private layoutContent(): number {
    const v = this.view;
    const w = this.contentW;
    const cx = w / 2;
    const mode = v.mode;
    const all = [
      this.haveRow,
      this.lead,
      this.picture,
      this.lockIcon,
      this.nameRow,
      this.keep,
      this.gaugeLabel,
      this.gaugeCount,
      this.gauge,
      this.toGoRow,
      this.hint,
      this.footnote,
      this.cancelButton,
      this.actionButton,
    ];
    for (const item of all) item.visible = false;
    this.checkIcon.visible = false;
    this.actionGem.visible = false;
    let y = 0;
    const gap = (n: number) => (y += n);
    const text = (t: Text, value: string, spacing = 6) => {
      t.text = value;
      t.style.wordWrapWidth = w;
      t.anchor.set(0.5, 0);
      t.position.set(cx, y);
      t.visible = true;
      y += t.height + spacing;
    };
    // Words, a gem, and any words after it, centred as one line.
    const gemRow = (row: Container, label: Text, gem: Sprite, value: string, gemSize: number, spacing = 6, tail: Text | null = null) => {
      label.text = value;
      gem.height = gemSize;
      gem.scale.x = gem.scale.y;
      const tailW = tail ? tail.width + 6 : 0;
      const rowW = label.width + 6 + gem.width + tailW;
      const h = Math.max(label.height, gemSize);
      label.position.set(0, h / 2);
      gem.position.set(label.width + 6, h / 2);
      tail?.position.set(label.width + 6 + gem.width + 6, h / 2);
      row.position.set(Math.round(cx - rowW / 2), y);
      row.visible = true;
      y += h + spacing;
    };
    const showPicture = (size: number) => {
      if (this.picture.texture && this.picture.texture.width > 1) {
        const s = size / Math.max(this.picture.texture.width, this.picture.texture.height);
        this.picture.scale.set(s);
        this.picture.position.set(cx, y);
        this.picture.visible = true;
        y += this.picture.height + 4;
      } else {
        const s = (size * 0.6) / Math.max(1, this.lockIcon.texture.height);
        this.lockIcon.scale.set(s);
        this.lockIcon.position.set(cx, y + size * 0.1);
        this.lockIcon.visible = true;
        y += size * 0.8 + 4;
      }
    };
    const name = (size: number, withCheck = false) => {
      this.nameText.text = v.name;
      this.nameText.style.fontSize = size;
      this.nameText.style.wordWrapWidth = withCheck ? w - 40 : w;
      const icon = withCheck && this.art.check ? 28 : 0;
      if (icon) {
        this.checkIcon.height = icon;
        this.checkIcon.scale.x = this.checkIcon.scale.y;
        this.checkIcon.visible = true;
      }
      const iconW = icon ? this.checkIcon.width + 8 : 0;
      const h = Math.max(this.nameText.height, icon);
      this.checkIcon.position.set(0, h / 2);
      this.nameText.position.set(iconW, h / 2);
      this.nameText.style.align = 'center';
      this.nameRow.position.set(Math.round(cx - (iconW + this.nameText.width) / 2), y);
      this.nameRow.visible = true;
      y += h + 4;
    };
    const gaugeBlock = (count: string | null) => {
      if (count !== null) {
        this.gaugeLabel.anchor.set(0, 1);
        this.gaugeCount.anchor.set(1, 1);
        this.gaugeCount.text = count;
        const rowH = Math.max(this.gaugeLabel.height, this.gaugeCount.height);
        this.gaugeLabel.position.set(2, y + rowH);
        this.gaugeCount.position.set(w - 2, y + rowH);
        this.gaugeLabel.visible = true;
        this.gaugeCount.visible = true;
        y += rowH + 4;
      }
      this.gauge.resize(w, 26);
      this.gauge.position.set(0, y);
      this.gauge.visible = true;
      y += 26 + 10;
    };
    const buttons = (cancel: string | null, action: string, withGem: boolean) => {
      this.actionButton.setLabel(action);
      this.actionButton.children.find((c): c is Text => c instanceof Text)?.scale.set(1);
      if (cancel) {
        this.cancelButton.setLabel(cancel);
        const cancelW = Math.round(Math.min(124, (w - GAP) * 0.38));
        this.cancelButton.resize(cancelW, BUTTON_H);
        this.actionButton.resize(w - GAP - cancelW, BUTTON_H);
        this.cancelButton.position.set(0, y);
        this.actionButton.position.set(cancelW + GAP, y);
        this.cancelButton.visible = true;
      } else {
        this.actionButton.resize(w, BUTTON_H);
        this.actionButton.position.set(0, y);
      }
      this.actionButton.visible = true;
      if (withGem && this.art.gem) this.placeActionGem();
      y += BUTTON_H + 3;
    };

    switch (mode) {
      case 'offer':
        gemRow(this.haveRow, this.haveText, this.haveGem, `You have ${v.gems.toLocaleString('en-GB')}`, 22, 2);
        text(this.lead, 'Gems open new slots. You have enough for:', 6);
        showPicture(134);
        name(26);
        text(this.keep, 'Yours to keep, forever.', 14);
        buttons('Not now', `OPEN IT FOR ${v.price}`, true);
        break;
      case 'unlock':
        showPicture(112);
        name(26);
        text(this.keep, 'Yours to keep, forever.', 12);
        gaugeBlock(`${v.gems.toLocaleString('en-GB')} / ${v.price}`);
        gap(4);
        buttons('Not now', `UNLOCK FOR ${v.price}`, true);
        break;
      case 'short': {
        showPicture(112);
        name(26);
        text(this.keep, 'Yours to keep, forever.', 12);
        gaugeBlock(`${v.gems.toLocaleString('en-GB')} / ${v.price}`);
        gemRow(this.toGoRow, this.toGoText, this.toGoGem, `${Math.max(0, v.price - v.gems).toLocaleString('en-GB')} more`, 22, 2, this.toGoTail);
        text(this.hint, unlockGemHint(v.reelGems === true), 14);
        buttons(null, 'KEEP PLAYING', false);
        break;
      }
      case 'downloading': {
        name(24);
        text(this.lead, "Fetching the slot's art and sound.\nAlmost there.", 12);
        gaugeBlock(null);
        const p = v.progress;
        const files = p ? `${p.filesLoaded}/${p.filesTotal} files` : 'Starting…';
        const bytes = p && p.bytesLoaded > 0 ? `  ·  ${formatBytes(p.bytesLoaded)}${p.bytesTotal > 0 ? ` / ${formatBytes(p.bytesTotal)}` : ''}` : '';
        y -= 4;
        text(this.footnote, files + bytes, 0);
        break;
      }
      case 'ready':
        gap(4);
        name(26, true);
        text(this.lead, 'Loading the game…', 2);
        gap(4);
        break;
      case 'none':
        text(this.lead, "This tile isn't a slot.", 14);
        buttons(null, 'CLOSE', false);
        break;
    }
    return Math.max(40, Math.round(y));
  }

  /** A gem after the action's words, the two centred together on the
   *  plate (the button centres its words alone, so they move left). */
  private placeActionGem(): void {
    const caption = this.actionButton.children.find((c): c is Text => c instanceof Text);
    if (!caption) return;
    // On a narrow phone the words and gem shrink a little to stay clear of
    // the plate's round ends.
    caption.scale.set(1);
    const full = caption.width + 6 + 22;
    const room = this.actionButton.buttonWidth - 36;
    const k = full > room ? Math.max(0.78, room / full) : 1;
    caption.scale.set(k);
    this.actionGem.height = 22 * k;
    this.actionGem.scale.x = this.actionGem.scale.y;
    const total = caption.width + 6 * k + this.actionGem.width;
    const left = this.actionButton.buttonWidth / 2 - total / 2;
    caption.x = left + caption.width / 2;
    this.actionGem.position.set(left + caption.width + 6 * k, BUTTON_H / 2);
    this.actionGem.visible = true;
  }
}

/** A byte count as KB or MB. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}
